import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, CheckSquare, Users, Sparkles, ArrowRight, Star } from "lucide-react";
import api from "../../lib/api";
import { useSubscription } from "../../hooks/useSubscription";
import { EmployerTabs } from "../../components/employer/EmployerTabs";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-5" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center mb-3">
      <Icon className="w-5 h-5 text-brand" />
    </div>
    <div className="font-heading text-3xl font-bold text-stone-text">{value}</div>
    <div className="text-sm text-stone-muted">{label}</div>
  </div>
);

export default function EmployerOverview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentPlan, trialDaysLeft, sub } = useSubscription();
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState(0);
  const [cands, setCands] = useState(0);

  useEffect(() => {
    api.get("/employer/jobs").then(({ data }) => setJobs(data || [])).catch(() => {});
    api.get("/employer/applications").then(({ data }) => setApps(data?.count || 0)).catch(() => {});
    api.get("/job-requests").then(({ data }) => setCands(data?.total || 0)).catch(() => {});
  }, []);

  const planName = currentPlan?.name || "—";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("employer.overview.title")}</h1>
      <EmployerTabs />

      {sub?.status === "trialing" && trialDaysLeft != null && (
        <div className="bg-brand text-white rounded-2xl p-5 mb-6 flex items-center justify-between gap-4" data-testid="trial-banner">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <div className="font-heading font-bold text-lg">{t("employer.overview.trialActive")}</div>
              <div className="text-white/85 text-sm">
                {t("employer.overview.trialDays", { days: trialDaysLeft })}
                {sub.trial_ends_at ? ` · ${t("employer.overview.trialEnds", { date: new Date(sub.trial_ends_at).toLocaleDateString(i18n.language) })}` : ""}
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/employer/plans")} className="hidden sm:flex items-center gap-1 bg-white text-brand font-semibold px-4 py-2 rounded-xl hover:-translate-y-0.5 transition-all">
            {t("employer.overview.upgradePlan")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Star} label={t("employer.overview.activePlan")} value={planName} testid="kpi-plan" />
        <Kpi icon={Briefcase} label={t("employer.overview.jobsPosted")} value={jobs.length} testid="kpi-jobs" />
        <Kpi icon={CheckSquare} label={t("employer.overview.applicationsReceived")} value={apps} testid="kpi-apps" />
        <Kpi icon={Users} label={t("employer.overview.candidatesAvailable")} value={cands} testid="kpi-cands" />
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={() => navigate("/employer/jobs")} className="px-5 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors">
          {t("employer.overview.manageJobs")}
        </button>
        <button onClick={() => navigate("/employer/applications")} className="px-5 py-3 rounded-xl bg-white border border-stone-border font-semibold text-stone-sub hover:border-brand hover:text-brand transition-colors">
          {t("employer.overview.viewApplications")}
        </button>
      </div>
    </div>
  );
}
